import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { HOME_PAGE } from '@/constants/testIds';
import {
  ArrowRight,
  Search,
  Sparkles,
  Send,
  Bell,
  ShieldCheck,
  Brain,
  Target,
  Plus,
  Store,
  Gavel,
  Building2,
  Briefcase,
  Users,
  Menu,
  X,
} from 'lucide-react';

/**
 * Landing — public, unauthenticated. This is the PUBLIC face of Zita only —
 * per the "UI Separation Requirement" the dashboard, search, results,
 * detail, settings, and membership all live on their own dedicated pages
 * (see App.js routes).
 *
 * Color hierarchy (locked Feb 2026):
 *   - ORANGE  = call-to-action (Start Trial, search arrow)
 *   - NAVY/BLUE = Zita platform (branding, wordmark, body text)
 *   - PURPLE = Ari (the AI card, its input, its suggestion chips)
 *   - WHITE  = clean workspace background with a very subtle gradient
 *
 * Source logos are real brand marks pulled from the Simple Icons CDN (CC0
 * with brand-color rendering). Non-brand-specific categories keep neutral
 * lucide icons so we don't misrepresent a category as a single company.
 */

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const primary = user ? '/seek' : '/register';

  return (
    <div className="min-h-screen flex flex-col zita-bg-cloud">
      <Header
        user={user}
        navigate={navigate}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      <main className="flex-1">
        {/* HERO */}
        <section
          data-testid={HOME_PAGE.hero}
          className="max-w-7xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 pb-14 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start"
        >
          {/* LEFT — headline + copy + CTAs */}
          <div className="zita-fade-up">
            <h1 className="font-serif tracking-tight text-6xl sm:text-7xl lg:text-8xl leading-[0.98] zita-ink">
              Find anything.
              <br />
              <span className="zita-orange-text">Faster.</span>
            </h1>
            <p className="mt-8 text-lg zita-ink-soft max-w-xl leading-relaxed">
              Type what you&apos;re looking for the way you&apos;d say it out loud.
              Zita searches thousands of websites, marketplaces, stores, groups,
              directories, and job boards, then hands you the best matches — organized,
              exportable, and ready for whatever you want to do next.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={primary}
                data-testid={HOME_PAGE.getStarted}
                className="zita-btn-orange"
              >
                <Sparkles size={16} />
                Start 3-day free trial
              </Link>
              <button
                type="button"
                onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}
                className="zita-btn-ghost-navy inline-flex items-center gap-2"
                data-testid="landing-see-how"
              >
                <Search size={14} /> See how it works
              </button>
            </div>
            <div className="mt-4 text-xs zita-ink-muted">
              3-day free trial · no credit card required · $29.99/month after · cancel anytime
            </div>
          </div>

          {/* RIGHT — Ari mockup input (PURPLE brand — AI) */}
          <div
            className="zita-premium-card zita-ari-card-halo p-6 sm:p-7 zita-fade-up"
            data-testid="landing-ari-mockup"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full zita-ari-avatar flex items-center justify-center">
                  <Sparkles size={16} />
                </div>
                <div className="text-base font-semibold ari-accent">Just tell Ari what you need</div>
              </div>
              <div className="flex items-center gap-2 text-xs zita-ink-muted">
                <div className="relative">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-500 zita-pulse-ring" />
                </div>
                Ari is online
              </div>
            </div>

            <div className="relative">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 ari-accent"
                aria-hidden="true"
              />
              <input
                type="text"
                readOnly
                placeholder="Ask Ari anything…"
                aria-label="Ari search preview (sign in to run real searches)"
                className="zita-ari-input w-full pl-11 pr-14 py-3.5 text-sm"
                onClick={() => navigate(primary)}
                data-testid="landing-ari-input"
              />
              {/* Circular purple send — mirrors the RemodDash Ari widget's send button. */}
              <button
                type="button"
                onClick={() => navigate(primary)}
                aria-label="Ask Ari"
                data-testid="landing-ari-send"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 zita-ari-send"
              >
                <Send size={16} />
              </button>
            </div>

            <div className="mt-5">
              <div className="text-xs zita-ink-muted mb-3">Try searching for:</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EXAMPLE_CHIPS.map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => navigate(primary)}
                    className="zita-ari-chip"
                  >
                    <Icon size={14} className="zita-ari-chip-icon" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SOURCES row — real brand logos where available */}
        <section
          id="sources"
          className="max-w-7xl mx-auto px-4 sm:px-8 pb-14 zita-fade-up"
        >
          <div className="text-center text-sm zita-ink-soft mb-5">
            Zita searches across <span className="font-semibold zita-ink">50+ sources</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {BRAND_SOURCES.map((src) => (
              <SourceChip key={src.label} {...src} />
            ))}
            {CATEGORY_SOURCES.map((src) => (
              <SourceChip key={src.label} {...src} />
            ))}
            <button
              type="button"
              className="zita-source-chip zita-orange-text font-medium"
              onClick={() => navigate('/connect')}
              data-testid="landing-more-sources"
            >
              <Plus size={16} /> More
            </button>
          </div>
        </section>

        {/* FEATURES strip — plain English, no jargon. */}
        <section
          id="how"
          className="max-w-7xl mx-auto px-4 sm:px-8 pb-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <Feature
            icon={Brain}
            title="Search the way you talk"
            body="Just type what you're looking for. No special search tricks or complicated commands to learn — Ari does the hard work."
            tint="ari"
          />
          <Feature
            icon={Target}
            title="One search, everywhere at once"
            body="Zita searches thousands of websites, marketplaces, businesses, jobs, stores, and groups — all from one place."
            tint="orange"
          />
          <Feature
            icon={Bell}
            title="Never miss a good one"
            body="Save a search and Zita quietly keeps looking. It pings you the moment a new match shows up."
            tint="orange"
          />
          <Feature
            icon={ShieldCheck}
            title="Your searches stay yours"
            body="Zita never sees the passwords you use on other sites, and no one else can see what you're looking for."
            tint="navy"
          />
        </section>

        {/* SCOEA — how Zita actually works, in plain English. */}
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] uppercase tracking-[0.18em] zita-ink-muted">How Zita works</div>
            <h2 className="mt-2 font-serif text-4xl zita-ink">Find. Save. Organize. Export. Act.</h2>
            <p className="mt-4 text-base zita-ink-soft">
              Zita does the finding. You decide what to do with it. Buy, call, apply,
              email, visit, share — Zita just makes sure the right stuff lands in front of you.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {SCOEA_STEPS.map((step, i) => (
              <div key={step.label} className="zita-premium-card p-5 relative">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] zita-ink-muted mb-2">
                  Step {i + 1}
                </div>
                <div className="text-lg font-semibold zita-ink">{step.label}</div>
                <p className="mt-2 text-sm zita-ink-soft leading-relaxed">{step.body}</p>
                {step.isYou && (
                  <span className="mt-3 inline-block text-[10px] uppercase tracking-widest zita-orange-text font-semibold">
                    You do this
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      <EcosystemFooter />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { label: 'How It Works', to: '#how',        kind: 'anchor' },
  { label: 'Features',     to: '#how',        kind: 'anchor' },
  { label: 'Sources',      to: '#sources',    kind: 'anchor' },
  { label: 'Pricing',      to: '/billing',    kind: 'link'   },
  { label: 'About',        to: '/about',      kind: 'link'   },
];

const Header = ({ user, navigate, menuOpen, setMenuOpen }) => {
  // Lock body scroll while the mobile sheet is open, and close on Esc.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, setMenuOpen]);

  return (
    <>
      <header className="max-w-7xl w-full mx-auto flex items-center justify-between px-4 sm:px-8 pt-4 sm:pt-6 pb-2 gap-3">
        {/* Logo — smaller on mobile so it never pushes actions off-screen. */}
        <Link to="/" className="flex items-center gap-2 lg:gap-4 group min-w-0 shrink" data-testid="landing-logo">
          <div className="relative -my-2 -ml-1 shrink-0">
            <ZitaLogo
              size={40}
              feathered
              className="drop-shadow-none lg:hidden"
            />
            <ZitaLogo
              size={130}
              feathered
              className="drop-shadow-none hidden lg:block"
            />
          </div>
          <div className="flex flex-col leading-none min-w-0">
            <ZitaWordmark className="text-2xl lg:text-6xl zita-ink truncate" />
            <span className="hidden lg:inline text-[10px] uppercase tracking-[0.22em] zita-ink-muted mt-2">
              Powered by <span className="remod-accent font-semibold">RemodWorks</span>
            </span>
          </div>
        </Link>

        {/* Desktop nav — hidden below 1024px so tablets and large phones use the mobile sheet. */}
        <nav className="hidden lg:flex items-center gap-8 text-[15px] zita-ink-soft">
          {NAV_ITEMS.map((n) =>
            n.kind === 'anchor' ? (
              <a
                key={n.label}
                href={n.to}
                className="hover:zita-ink transition-colors"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {n.label}
              </a>
            ) : (
              <Link
                key={n.label}
                to={n.to}
                className="hover:zita-ink transition-colors"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {n.label}
              </Link>
            ),
          )}
        </nav>

        {/* Right side — DESKTOP: Sign in + Start 3-day trial.  MOBILE: compact Sign in + hamburger. */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user ? (
            <button
              type="button"
              onClick={() => navigate('/seek')}
              className="zita-btn-orange text-sm sm:text-base px-3 sm:px-5 py-2 sm:py-3"
              data-testid="landing-open-zita"
            >
              Open Zita <ArrowRight size={14} />
            </button>
          ) : (
            <>
              {/* Compact Sign in — always visible, small on mobile. */}
              <Link
                to="/login"
                className="text-sm sm:text-base zita-ink-soft hover:zita-ink transition-colors px-2 py-1 sm:px-3 sm:py-2"
                data-testid={HOME_PAGE.loginLink}
              >
                Sign in
              </Link>
              {/* Start-trial button ONLY on desktop — never in the mobile / tablet header. */}
              <Link
                to="/register"
                className="hidden lg:inline-flex zita-btn-orange"
                data-testid="landing-register"
              >
                Start 3-day trial
              </Link>
            </>
          )}

          {/* Hamburger menu — mobile + tablet portrait (any width <1024px). */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg zita-ink hover:bg-zita-sand/60 transition-colors"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            data-testid="landing-mobile-menu-toggle"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* MOBILE / TABLET MENU SHEET */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          {/* Scrim */}
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMenuOpen(false)}
            data-testid="landing-mobile-menu-scrim"
          />
          {/* Panel */}
          <aside
            className="absolute right-0 top-0 h-full w-4/5 max-w-sm bg-white shadow-2xl flex flex-col"
            data-testid="landing-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b zita-hairline-border">
              <ZitaWordmark className="text-2xl zita-ink" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                data-testid="landing-mobile-menu-close"
                className="w-10 h-10 inline-flex items-center justify-center rounded-lg hover:bg-zita-sand/60"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {NAV_ITEMS.map((n) =>
                n.kind === 'anchor' ? (
                  <a
                    key={n.label}
                    href={n.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="block px-3 py-3 rounded-lg text-base zita-ink hover:bg-zita-sand/60"
                  >
                    {n.label}
                  </a>
                ) : (
                  <Link
                    key={n.label}
                    to={n.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="block px-3 py-3 rounded-lg text-base zita-ink hover:bg-zita-sand/60"
                  >
                    {n.label}
                  </Link>
                ),
              )}
            </nav>
            {!user && (
              <div className="px-5 py-4 border-t zita-hairline-border space-y-2">
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="zita-btn-orange w-full justify-center"
                  data-testid="mobile-nav-register"
                >
                  Start 3-day free trial
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="zita-btn-ghost-navy w-full justify-center"
                  data-testid="mobile-nav-signin"
                >
                  Sign in
                </Link>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
};

// Real brand logo via Simple Icons CDN (SVG). Optional `color` override — by
// default Simple Icons renders in the brand's official color.
const BrandLogo = ({ slug, size = 18, color }) => (
  <img
    src={`https://cdn.simpleicons.org/${slug}${color ? `/${color}` : ''}`}
    alt=""
    width={size}
    height={size}
    loading="lazy"
    draggable={false}
    style={{ width: size, height: size }}
  />
);

// Craigslist and OfferUp aren't on Simple Icons — we render their brand marks
// inline so the sources row stays visually consistent instead of showing
// broken image icons. Colors match each brand's public identity.
const CraigslistMark = ({ size = 18 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    style={{ color: '#5B21B6' }}
  >
    {/* Purple 'CL' peace-sign glyph — stylised abbreviation. */}
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontSize="10"
      fontWeight="800"
      fill="white"
      fontFamily="ui-sans-serif, system-ui, -apple-system, Helvetica, sans-serif"
    >
      CL
    </text>
  </svg>
);

const OfferUpMark = ({ size = 18 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    style={{ color: '#00B25F' }}
  >
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontSize="10"
      fontWeight="800"
      fill="white"
      fontFamily="ui-sans-serif, system-ui, -apple-system, Helvetica, sans-serif"
    >
      OU
    </text>
  </svg>
);

const SourceChip = ({ label, kind, slug, brandColor, icon: Icon, iconColor, mark: Mark }) => (
  <div className="zita-source-chip">
    {kind === 'brand' && Mark ? (
      <Mark size={16} />
    ) : kind === 'brand' ? (
      <BrandLogo slug={slug} size={16} color={brandColor} />
    ) : (
      <Icon size={16} style={{ color: iconColor }} />
    )}
    <span>{label}</span>
  </div>
);

const Feature = ({ icon: Icon, title, body, tint = 'orange' }) => {
  const bgClass =
    tint === 'ari'
      ? 'bg-[hsl(var(--ari-purple)/0.10)] ari-accent'
      : tint === 'navy'
      ? 'bg-[hsl(220_45%_95%)] zita-ink'
      : 'bg-[hsl(var(--zita-orange-soft))] zita-orange-text';
  return (
    <div className="zita-premium-card p-6">
      <div className={`w-10 h-10 rounded-full ${bgClass} flex items-center justify-center mb-3`}>
        <Icon size={18} />
      </div>
      <div className="text-base font-semibold zita-ink">{title}</div>
      <p className="mt-2 text-sm zita-ink-soft leading-relaxed">{body}</p>
    </div>
  );
};

const EcosystemFooter = () => {
  const { user } = useAuth();
  return (
    <footer className="border-t zita-hairline-border bg-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        {!user && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-center">
            <Link
              to="/register"
              className="zita-btn-orange justify-center"
              data-testid="footer-register"
            >
              <Sparkles size={16} /> Start 3-day free trial
            </Link>
            <Link
              to="/login"
              className="zita-btn-ghost-navy justify-center"
              data-testid="footer-signin"
            >
              Sign in
            </Link>
          </div>
        )}
        <nav
          className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm zita-ink-soft"
          data-testid="footer-nav"
        >
          <a href="#how" className="hover:zita-ink" data-testid="footer-how">How It Works</a>
          <a href="#how" className="hover:zita-ink" data-testid="footer-features">Features</a>
          <a href="#sources" className="hover:zita-ink" data-testid="footer-sources">Sources</a>
          <Link to="/billing" className="hover:zita-ink" data-testid="footer-pricing">Pricing</Link>
          <Link to="/about" className="hover:zita-ink" data-testid="footer-about">About</Link>
          <Link to="/privacy" className="hover:zita-ink" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/terms" className="hover:zita-ink" data-testid="footer-terms">Terms and Conditions</Link>
          <a href="mailto:support@zitaview.com" className="hover:zita-ink" data-testid="footer-support">
            Support
          </a>
        </nav>
        <div className="text-center text-xs zita-ink-muted">
          © 2026 Zita — Find Anything. Faster. ·{' '}
          <span className="zita-ink-soft">
            Part of the <span className="remod-accent font-semibold">RemodWorks</span> AI Ecosystem
          </span>
        </div>
      </div>
    </footer>
  );
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

// Ari's suggested searches — icons are lucide, styled purple via .zita-ari-chip
import {
  Home as HomeIcon,
  Warehouse,
  Star,
  Truck,
} from 'lucide-react';

const EXAMPLE_CHIPS = [
  { label: 'Free refrigerators',      icon: HomeIcon  },
  { label: 'Manufacturing companies', icon: Building2 },
  { label: 'Warehouse jobs',          icon: Warehouse },
  { label: 'Walmart clearance',       icon: Star      },
  { label: 'Estate sales',            icon: HomeIcon  },
  { label: 'Box trucks',              icon: Truck     },
];

// Real brand logos — Simple Icons CDN. Slug = the icon's kebab-case name at
// simpleicons.org. Brand color is rendered by default; override only when a
// brand's own color would be invisible on our white chip.
const BRAND_SOURCES = [
  { kind: 'brand', label: 'Facebook',   slug: 'facebook'   },
  { kind: 'brand', label: 'Craigslist', mark: CraigslistMark },
  { kind: 'brand', label: 'OfferUp',    mark: OfferUpMark    },
  { kind: 'brand', label: 'Nextdoor',   slug: 'nextdoor'   },
];

// Category groupings — not a single company, so we use neutral lucide icons.
const CATEGORY_SOURCES = [
  { kind: 'category', label: 'Retailers',  icon: Store,     iconColor: '#F97316' },
  { kind: 'category', label: 'Auctions',   icon: Gavel,     iconColor: '#B45309' },
  { kind: 'category', label: 'Businesses', icon: Building2, iconColor: '#334155' },
  { kind: 'category', label: 'Jobs',       icon: Briefcase, iconColor: '#334155' },
  { kind: 'category', label: 'Groups',     icon: Users,     iconColor: '#0EA5E9' },
];

// SCOEA — Source → Collect → Organize → Export → Act. The last step is YOU.
// Zita finds and prepares; the user decides what to do with the information.
const SCOEA_STEPS = [
  {
    label: 'Find',
    body: 'Tell Ari what you\'re looking for and Zita searches every supported source at once.',
  },
  {
    label: 'Save',
    body: 'Bookmark anything worth keeping. Save one match or every match with a single click.',
  },
  {
    label: 'Organize',
    body: 'Group your saves into folders. Ari can even suggest where each item belongs.',
  },
  {
    label: 'Export',
    body: 'Download your list as Excel, CSV, or PDF — open it in any app you already use.',
  },
  {
    label: 'Act',
    body: 'Buy it, call, email, visit, apply, or share. Zita hands you the info — you take it from there.',
    isYou: true,
  },
];

export default Landing;
